import React, { createContext, useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { API } from '../services';
import { ErrorMessageContext } from './ErrorMessageContext';
import { DEVICE_STATUS_KEYS } from '../utilities/constants';
import { getErrorMessage } from '../utilities/commonFunctions';

const DeviceManagementContext = createContext(null);

export const DeviceManagementProvider = ({ children }) => {
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [onboardedDeviceIds, setOnboardedDeviceIds] = useState([]);
  const [installedDeviceIds, setInstalledDeviceIds] = useState([]);
  const [replacedDeviceIds, setReplacedDeviceIds] = useState([]);
  const [uninstalledDeviceIds, setUninstalledDeviceIds] = useState([]);
  const [multiOrgInstalledDeviceIds, setMultiOrgInstalledDeviceIds] = useState(
    []
  );
  const [orgBranchList, setOrgBranchList] = useState({});
  const [devicesPresent, setDevicesPresent] = useState(false);

  const fetchDeviceIds = async (deviceStatus, setState, extraFilters = {}) => {
    let page = 1;
    const uniqueDeviceIds = new Set();

    while (page !== null) {
      try {
        const { data } = await API.getDevicesList(page, 100, {
          filter: {
            device_status: [deviceStatus],
            ...extraFilters,
          },
        });

        if (data?.results) {
          data?.results?.forEach(item => {
            if (item?.device_id) {
              uniqueDeviceIds.add(item.device_id);
            }
          });

          const uniqueData = Array.from(uniqueDeviceIds).sort(() => 0);
          setState(
            uniqueData.map(vehicle => ({
              label: vehicle,
              value: vehicle,
            }))
          );

          page = data?.next_page_number;
        } else {
          page = null;
        }
      } catch (error) {
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch vehicle list! Please try again after sometime.'
          )
        );
        page = null;
      }
    }
  };

  const fetchOnboardedDeviceIds = () => {
    fetchDeviceIds(DEVICE_STATUS_KEYS.ONBOARDED, setOnboardedDeviceIds);
  };

  const fetchInstalledDeviceIds = (extraFilters = {}) => {
    fetchDeviceIds(
      DEVICE_STATUS_KEYS.INSTALLED,
      setInstalledDeviceIds,
      extraFilters
    );
  };

  const fetchReplacedDeviceIds = () => {
    fetchDeviceIds(DEVICE_STATUS_KEYS.REPLACED, setReplacedDeviceIds);
  };

  const fetchUninstalledDeviceIds = () => {
    fetchDeviceIds(DEVICE_STATUS_KEYS.UNINSTALLED, setUninstalledDeviceIds);
  };

  const fetchMultiOrgInstalledDeviceIds = () => {
    fetchDeviceIds(
      DEVICE_STATUS_KEYS.INSTALLED,
      setMultiOrgInstalledDeviceIds,
      { multi_org_commissioned: true }
    );
  };

  const generateOrgBranchList = orgs => {
    return orgs.reduce((map, { org, location = [] }) => {
      map[org] = location
        .filter(loc => loc.branch_name)
        .map(loc => loc.branch_name);
      return map;
    }, {});
  };

  const fetchOrgBranchList = async () => {
    try {
      const { data } = await API.getCompaniesList();
      if (data) {
        setOrgBranchList(generateOrgBranchList(data));
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to fetch companies list! Please try again after sometime.'
        )
      );
    }
  };

  const checkDevicesPresent = async () => {
    try {
      const { data } = await API.getDevicesList(1, 10, {});
      if (data?.count > 0) {
        setDevicesPresent(true);
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to fetch devices list! Please try again after sometime.'
        )
      );
    }
  };

  const contextValue = useMemo(
    () => ({
      fetchOnboardedDeviceIds,
      onboardedDeviceIds,
      fetchOrgBranchList,
      orgBranchList,
      fetchInstalledDeviceIds,
      installedDeviceIds,
      devicesPresent,
      checkDevicesPresent,
      fetchReplacedDeviceIds,
      replacedDeviceIds,
      fetchUninstalledDeviceIds,
      uninstalledDeviceIds,
      fetchMultiOrgInstalledDeviceIds,
      multiOrgInstalledDeviceIds,
    }),
    [
      onboardedDeviceIds,
      orgBranchList,
      installedDeviceIds,
      devicesPresent,
      replacedDeviceIds,
      uninstalledDeviceIds,
      multiOrgInstalledDeviceIds,
    ]
  );

  return (
    <DeviceManagementContext.Provider value={contextValue}>
      {children}
    </DeviceManagementContext.Provider>
  );
};

DeviceManagementProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useDeviceManagement = () => useContext(DeviceManagementContext);
